import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import React, { useEffect, useMemo, useState } from "react"
import BackButton from "components/Shared/BackButton"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { Tabs, Tab } from "react-bootstrap"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import {
  addLoyaltyPoints,
  deleteCustomer,
  getCustomerDetails,
  getCustomerLoyaltyPoints,
  getPaymentHistory,
  getWalletHistory,
  revokeLoyaltyPoints,
  updateCustomerStatus,
} from "store/actions/customersAction"
import { useLocation, useNavigate } from "react-router"
import MiniCards from "components/Shared/mini-card"
import { getAllOrders } from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import Paginations from "components/Common/Paginations"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import DeleteModal from "components/Common/DeleteModal"
import {
  canViewCustomerGiftCardUsage,
  canViewCustomerWalletHistory,
} from "common/customerDetailsPermissionUtils"
import { showAlert } from "store/reducers/alertReducer"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import {
  formatAppVersionLabel,
  formatDeviceLanguageLabel,
} from "utils/displayLabels"
import { ErrorMessage, Field, Form, Formik } from "lib/formik"
import {
  addLoyaltyPointSchema,
  LOYALTY_REMARK_MAX_LENGTH,
  revokeLoyaltyPointsSchema,
} from "components/Common/validation"
import { DatePicker } from "rsuite"
import { useTranslation } from "react-i18next"
import {
  disablePastDateTime,
  statusListMobileStatus,
  statusListSalasaStatus,
  statusListStatus,
} from "components/Common/constants"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"
import { getAllGiftCardsUsage } from "store/actions/giftCardAction"
import formatType from "helpers/helpers"
import {
  getBrandOrdersBasePath,
  useAssignedBrands,
} from "hooks/useAssignedBrands"
import {
  hasExternalPosIntegration,
  isSalasaProvider,
  isSyrveProvider,
} from "utils/orderPosUtils"
import { translateGender, translateGatewayStatus, translatePaymentGateway } from "utils/statusLabels"
import {
  getSystemStatusDisplay,
  resolveSystemOrderStatusValue,
} from "utils/orderStatusUtils"

const initialValues = {
  points: null,
  remarks: "",
}

const addLoyaltyPointsValues = {
  remarks: "",
  points: "",
  expiresAt: null,
}

const CustomersView = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Customer Details ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [activeTab, setActiveTab] = useState("DEVICE_INFO")
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const {
    loading,
    loyaltyLoading,
    error,
    isLoading,
    isUpdating,
    message,
    actionType,
    customerDetails,
    customerLoyaltyPoints,
    totalCount: loyaltyTotalCount,
    totalPages: loyaltyTotalPages,
    currentPage: loyaltyCurrentPage,
    paymentHistory,
    paymentHistoryLoading,
    paymentCurrentPage,
    paymentTotalPages,
    paymentTotalCount,
    walletHistory,
    walletHistoryLoading,
    walletTotalPages,
    walletCurrentPage,
    walletTotalCount,
  } = useSelector(state => state.Customers)

  const {
    loading: orderLoading,
    isLoading: orderIsLoading,
    isUpdating: orderIsUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)

  const {
    loading: giftCardLoading,
    totalCount: giftCardTotalCount,
    totalPages: giftCardTotalPages,
    currentPage: giftCardCurrentPage,
    itemsPerPage: giftCardItemsPerPage,
    giftCardsUsage,
  } = useSelector(state => state.GiftCard)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const { brands, defaultBrandId } = useAssignedBrands()
  const hasPos = useMemo(
    () => brands.some(brand => hasExternalPosIntegration(brand)),
    [brands]
  )

  const resolveOrderBrand = item =>
    brands.find(brand => String(brand.brandId) === String(item?.brandId))

  const resolveOrderServiceProvider = item => {
    if (item?.serviceProvider) {
      return item.serviceProvider
    }
    const brand = resolveOrderBrand(item)
    return brand?.serviceProvider || brand?.integrationCode || ""
  }

  const orderHasPos = item => {
    const brand = resolveOrderBrand(item)
    return (
      hasExternalPosIntegration(item) || hasExternalPosIntegration(brand || {})
    )
  }

  const isSalasaOrder = item => {
    const brand = resolveOrderBrand(item)
    return (
      isSalasaProvider(item) ||
      isSalasaProvider(brand) ||
      item?.serviceProvider === "Salasa" ||
      brand?.serviceProvider === "Salasa"
    )
  }

  const isSyrveOrder = item => {
    const brand = resolveOrderBrand(item)
    return (
      isSyrveProvider(item) ||
      isSyrveProvider(brand) ||
      item?.serviceProvider === "Syrve" ||
      brand?.serviceProvider === "Syrve"
    )
  }

  const getOrderStatusDisplay = item => {
    if (isSalasaOrder(item)) {
      const failedToSalasa =
        (Number(item?.orderStatus) === 1 ||
          resolveSystemOrderStatusValue(item) === 1) &&
        item?.paymentStatus === 1 &&
        item?.salasaOrderId === null &&
        item?.sentToSalasaFlag
      if (failedToSalasa) {
        return {
          text: t("Sent To Salasa Failed"),
          color: statusListSalasaStatus[6]?.color,
        }
      }
      // Show system status in customer order list (POS is portal-only detail)
      return getSystemStatusDisplay(
        { ...item, brandIntegration: "Salasa" },
        t
      )
    }

    return getSystemStatusDisplay(
      {
        ...item,
        brandIntegration: isSyrveOrder(item)
          ? "Syrve"
          : item?.brandIntegration,
      },
      t
    )
  }

  const navigateToOrderDetails = item => {
    const brandId =
      item?.brandId != null && String(item.brandId).trim() !== ""
        ? String(item.brandId).trim()
        : resolveOrderBrand(item)?.brandId ||
          (defaultBrandId != null ? String(defaultBrandId).trim() : "")

    if (!brandId) {
      return
    }

    navigate(`${getBrandOrdersBasePath(brandId)}/order-details?id=${item._id}`)
  }

  const [customersPermissions, setCustomersPermissions] = useState({
    list: null,
    edit: null,
    block: null,
  })

  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [blockTarget, setBlockTarget] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [customerSubPermissions, setCustomerSubPermissions] = useState({})

  const canViewWalletHistory = useMemo(
    () => canViewCustomerWalletHistory(customerSubPermissions),
    [customerSubPermissions]
  )

  const canViewGiftCardUsage = useMemo(
    () =>
      canViewCustomerGiftCardUsage(
        customerSubPermissions,
        userRole,
        accountType,
        accountScope
      ),
    [customerSubPermissions, userRole, accountType, accountScope]
  )

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      7,
      "customersPermission_1",
      accountType
    )

    // get sub permissions array
    const subPermissionsView = getSubPermissions(
      userRole,
      7,
      "customersPermission_1",
      accountType,
      accountScope
    )

    // set sub permissions array data state
    setCustomerSubPermissions(subPermissionsView)

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      7,
      "customersPermission_2",
      accountType
    )

    const isPermissionBlock = isPermissionEnabled(
      userRole,
      7,
      "customersPermission_6",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setCustomersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
      block: isPermissionBlock,
    })
  }, [userRole, accountType, accountScope, navigate])

  useEffect(() => {
    fetchCustomerDetails()
    // fetchOrdersList()
    // fetchCustomerLoyaltyPoints()
  }, [])

  const fetchCustomerDetails = () => {
    const payload = {
      id,
    }
    dispatch(getCustomerDetails(payload))
  }

  const fetchCustomerLoyaltyPoints = tab => {
    if (!customerSubPermissions?.view_loyalty_points?.isEnabled) return
    const payload = {
      perPage: itemsPerPage,
      pageNo: tab === "LOYALTY" ? 1 : pageNumber,
      orderBy: 2,
      keyword: "",
      userId: id,
    }
    dispatch(getCustomerLoyaltyPoints(payload))
    navigate(
      `/customers/customer-details?id=${id}&page=${
        tab === "LOYALTY" ? 1 : pageNumber
      }`
    )
  }

  const fetchOrdersList = tab => {
    const canViewOrdersTab =
      isPermissionEnabled(userRole, 9, "order_1", accountType, accountScope) ||
      customerSubPermissions?.view_orders?.isEnabled
    if (!canViewOrdersTab) return
    const payload = {
      perPage: itemsPerPage,
      pageNo: tab === "ORDERS" ? 1 : pageNumber,
      orderBy: 2,
      keyword: "",
      userId: id,
      status: 0,
    }
    dispatch(getAllOrders(payload))
    navigate(
      `/customers/customer-details?id=${id}&page=${
        tab === "ORDERS" ? 1 : pageNumber
      }`
    )
  }

  const fetchPaymentHistory = tab => {
    if (!customerSubPermissions?.view_payment_history?.isEnabled) return
    const payload = {
      perPage: itemsPerPage,
      pageNo: tab === "PAYMENT_HISTORY" ? 1 : pageNumber,
      orderBy: 2,
      keyword: "",
      userId: id,
      dateRange: {},
      branchId: [],
    }
    dispatch(getPaymentHistory(payload))
    navigate(
      `/customers/customer-details?id=${id}&page=${
        tab === "PAYMENT_HISTORY" ? 1 : pageNumber
      }`
    )
  }

  const fetchWalletHistory = tab => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: tab === "WALLET_HISTORY" ? 1 : pageNumber,
      orderBy: 2,
      keyword: "",
      userId: id,
      dateRange: {},
      transactionType: 0, //1 credit, 2 debit, 0 all
    }
    dispatch(getWalletHistory(payload))
    navigate(
      `/customers/customer-details?id=${id}&page=${
        tab === "WALLET_HISTORY" ? 1 : pageNumber
      }`
    )
  }

  const fetchGiftCardUsage = tab => {
    const canViewGiftUsage =
      isPermissionEnabled(
        userRole,
        37,
        "gift_card_1",
        accountType,
        accountScope
      ) ||
      isPermissionEnabled(
        userRole,
        38,
        "gift_card_usage_1",
        accountType,
        accountScope
      ) ||
      canViewCustomerGiftCardUsage(
        customerSubPermissions,
        userRole,
        accountType,
        accountScope
      )
    if (!canViewGiftUsage) return
    const payload = {
      perPage: giftCardItemsPerPage,
      pageNo: tab === "GIFT_CARD_USAGE" ? 1 : pageNumber,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: "",
      receiverNumber: "",
      userId: id,
    }
    dispatch(getAllGiftCardsUsage(payload))
    navigate(
      `/customers/customer-details?id=${id}&page=${
        tab === "GIFT_CARD_USAGE" ? 1 : pageNumber
      }`
    )
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
    if (tab === "ORDERS") {
      navigate(`/customers/customer-details?id=${id}&page=1`)
      fetchOrdersList(tab)
    } else if (tab === "LOYALTY") {
      if (!customerSubPermissions?.view_loyalty_points?.isEnabled) return
      fetchCustomerLoyaltyPoints(tab)
    } else if (tab === "PAYMENT_HISTORY") {
      if (!customerSubPermissions?.view_payment_history?.isEnabled) return
      fetchPaymentHistory(tab)
    } else if (tab === "WALLET_HISTORY") {
      if (!canViewWalletHistory) return
      fetchWalletHistory(tab)
    } else if (tab === "RATINGS") {
    } else if (tab === "DEVICE_INFO") {
    } else if (tab === "GIFT_CARD_USAGE") {
      if (!canViewGiftCardUsage) return
      fetchGiftCardUsage(tab)
    }
  }

  const [miniCards, setMiniCards] = useState([])

  useEffect(() => {
    if (customerDetails) {
      const { ordersCount, totalPoints, totalAmount, totalWalletBalance } =
        customerDetails
      const cards = [
        {
          id: 1,
          title: t("Total Orders"),
          iconClass: "mdi mdi-cart-outline",
          text: ordersCount,
        },
        {
          id: 2,
          title: t("Total Loyalty Points"),
          iconClass: "mdi mdi-gift-outline",
          text: totalPoints > 0 ? totalPoints?.toFixed(2) : 0,
        },
        {
          id: 3,
          title: t("Total Amount Spent"),
          iconClass: "mdi mdi-wallet-outline",
          text: `${totalAmount?.toFixed(2)} ${t("SAR")}`,
        },
      ]

      if (canViewWalletHistory) {
        cards.push({
          id: 4,
          title: t("Total Wallet Balance"),
          iconClass: "bx bx-wallet",
          text: `${totalWalletBalance?.toFixed(2) || 0} ${t("SAR")}`,
        })
      }

      setMiniCards(cards)
    }
  }, [customerDetails, language, canViewWalletHistory, t])

  const formatPromiseDateTime = dateTimeString =>
    formatDateTime(dateTimeString)

  const onPageChange = page => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: 1,
      keyword: "",
      userId: id,
      status: 0,
    }
    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/customer-details?id=${id}&page=${page}`)
        }
      })
      .catch(error => {})
  }

  const onLoyaltyPageChange = page => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: 1,
      keyword: "",
      userId: id,
    }
    dispatch(getCustomerLoyaltyPoints(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/customer-details?id=${id}&page=${page}`)
        }
      })
      .catch(error => {})
  }

  const onPaymentHistoryPageChange = page => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: 2,
      keyword: "",
      userId: id,
      dateRange: {},
      branchId: [],
    }

    dispatch(getPaymentHistory(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/customer-details?id=${id}&page=${page}`)
        }
      })
      .catch(error => {})
  }

  const onWalletHistoryPageChange = page => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: 2,
      keyword: "",
      userId: id,
      dateRange: {},
      transactionType: 0, //1 credit, 2 debit, 0 all
    }

    dispatch(getWalletHistory(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/customer-details?id=${id}&page=${page}`)
        }
      })
      .catch(error => {})
  }

  const onGiftCardUsagePageChange = page => {
    const payload = {
      perPage: giftCardItemsPerPage,
      pageNo: page,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: "",
      receiverNumber: "",
      userId: id,
    }
    dispatch(getAllGiftCardsUsage(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/customer-details?id=${id}&page=${page}`)
        }
      })
      .catch(error => {})
  }

  const handleRevokeLoyaltyPoints = (values, resetForm) => {
    const { points, remarks } = values
    const payload = {
      remarks,
      points,
      customerId: id,
    }

    dispatch(revokeLoyaltyPoints(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          const currentLoyaltyPoints = Number(
            miniCards?.find(item => item.id === 2)?.text
          )
          const updatedLoyaltyPoints = currentLoyaltyPoints - points
          setMiniCards(prevCards =>
            prevCards?.map(card =>
              card.id === 2
                ? { ...card, text: updatedLoyaltyPoints.toFixed(2) }
                : card
            )
          )
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowModal(false)
          setModalType(null)
          resetForm()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleAddLoyaltyPoints = (values, { resetForm }) => {
    const { remarks, points, expiresAt } = values

    const payload = {
      remarks,
      points,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      customers: [id],
    }

    dispatch(addLoyaltyPoints(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          const currentLoyaltyPoints = Number(
            miniCards?.find(item => item.id === 2)?.text
          )
          const updatedLoyaltyPoints = currentLoyaltyPoints + points
          setMiniCards(prevCards =>
            prevCards?.map(card =>
              card.id === 2
                ? { ...card, text: updatedLoyaltyPoints.toFixed(2) }
                : card
            )
          )
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowModal(false)
          setModalType(null)
          resetForm()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleEnableCustomer = (id, status) => {
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateCustomerStatus(payload))
      .then(action => {
        const { statusCode, status: ok, msg } = action?.payload || {}
        if (statusCode === 200 && ok) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowBlockModal(false)
          setBlockTarget(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const openBlockConfirm = (id, status) => {
    setBlockTarget({ id, status: Number(status) })
    setShowBlockModal(true)
  }

  const handleConfirmBlockAction = () => {
    if (!blockTarget?.id) return
    handleEnableCustomer(blockTarget.id, blockTarget.status)
  }

  const isBlockAction = Number(blockTarget?.status) === 1
  const blockModalTitle = isBlockAction
    ? t("Are you sure you want to block this customer?")
    : t("Are you sure you want to unblock this customer?")
  const blockModalButton = isBlockAction ? t("Block") : t("Unblock")
  const blockModalIcon = isBlockAction
    ? "mdi mdi-account-cancel-outline"
    : "mdi mdi-account-check-outline"
  const blockModalBtnClass = isBlockAction
    ? "btn btn-warning"
    : "btn btn-success"

  if (loading) return <Spinners />

  return (
    <div className="page-content  customers_page">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl="7">
            <Card className="full_height_card">
              <CardBody>
                <CardTitle className="mb-4 d-flex align-items-center gap-2">
                  <BackButton fallbackTo="/customers" />
                  {t("Customer Information")}
                </CardTitle>

                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {Object.values(customerDetails).length === 0 ? (
                    <p className="text-center">
                      {t("No customer details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{customerDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {customersPermissions.block &&
                                customerDetails.status !== 2 && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      openBlockConfirm(
                                        id,
                                        customerDetails.status
                                      )
                                    }}
                                    isChecked={customerDetails.status === 1}
                                    disabled={isUpdating}
                                  />
                                )}
                              <div style={{ marginTop: 2 }}>
                                {customerDetails.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : customerDetails.status === 0 ? (
                                  <Badge className="bg-danger">
                                    {t("Blocked")}
                                  </Badge>
                                ) : customerDetails.status === 2 ? (
                                  <Badge className="bg-danger">
                                    {t("Deleted")}
                                  </Badge>
                                ) : (
                                  "-"
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("First Name")}</th>
                          <td>{customerDetails.firstName || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Last Name")}</th>
                          <td>{customerDetails.lastName || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Email")}</th>
                          <td>
                            {customerDetails.email || "-"}
                            {customerDetails.isEmailVerified && (
                              <i className="bx bxs-badge-check text-success mx-1"></i>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Gender")}</th>
                          <td>{translateGender(t, customerDetails.gender)}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("DOB")}</th>
                          <td>{customerDetails.dob || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Nationality")}</th>
                          <td>{customerDetails.nationality || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Social Id")}</th>
                          <td>{customerDetails.socialId || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Referral Code")}</th>
                          <td>{customerDetails.referralCode || "-"}</td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Phone Number")}</th>
                          <td>
                            {customerDetails.phoneCode ||
                            customerDetails.phoneNumber ? (
                              <LtrText>{`${customerDetails.phoneCode || ""}${
                                customerDetails.phoneNumber || ""
                              }`}</LtrText>
                            ) : (
                              "-"
                            )}
                            {customerDetails.isPhoneNoVerified && (
                              <i className="bx bxs-badge-check text-success mx-1"></i>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Login Type")}</th>
                          <td>
                            {customerDetails.loginType === "GOOGLE" ? (
                              <li className="social-list-item bg-danger text-white border-danger">
                                <i className="mdi mdi-google" />
                              </li>
                            ) : null}
                            {customerDetails.loginType === "APPLE" ? (
                              <li className="social-list-item bg-danger text-white border-danger">
                                <i className="mdi mdi-apple" />
                              </li>
                            ) : null}
                            {customerDetails.loginType ===
                            "PHONE_NUMBER_AND_PASSWORD" ? (
                              <li className="social-list-item bg-success text-white border-success">
                                <i className="mdi mdi-cellphone" />
                              </li>
                            ) : null}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Last Logged In")}</th>
                          <td>
                            {formatDateTime(customerDetails.lastLoggedIn)}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("created At")}</th>
                          <td>{formatDateTime(customerDetails.createdAt)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="5">
            {customersPermissions.list &&
              customerSubPermissions?.view_loyalty_points?.isEnabled && (
                <Card>
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between">
                      {customerDetails.totalPoints > 0 && (
                        <Button
                          color="warning"
                          onClick={() => {
                            setShowModal(true)
                            setModalType("REVOKE_LOYALTY_POINTS")
                          }}
                        >
                          {t("Revoke Loyalty Points")}
                        </Button>
                      )}
                      <Button
                        color="primary"
                        onClick={() => {
                          setShowModal(true)
                          setModalType("ADD_LOYALTY_POINTS")
                        }}
                      >
                        {t("Add Loyalty Points")}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}
            {(miniCards || [])?.map((card, key) => (
              <MiniCards
                title={card.title}
                text={card.text}
                iconClass={card.iconClass}
                key={"_card_" + key}
                cardCol={12}
              />
            ))}
          </Col>
        </Row>
        <Row>
          <Col>
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabChange}
              className="mb-2"
            >
              {(isTenantOwnerAdmin() ||
                (customersPermissions.list &&
                  customerSubPermissions?.view_device_information
                    ?.isEnabled)) && (
                <Tab eventKey="DEVICE_INFO" title={t("Device Information")}>
                  <Card style={{ maxHeight: 600 }}>
                    <CardBody>
                      {customerDetails && customerDetails.deviceInfos ? (
                        <Table className="table mb-0">
                          <tbody>
                            <tr>
                              <th scope="row">{t("S. No.")}</th>
                              <th>{t("Device Id")}</th>
                              <th>{t("Language")}</th>
                              <th>{t("App Version")}</th>
                              <th>{t("OS Version")}</th>
                              <th>{t("Platform")}</th>
                            </tr>

                            {customerDetails?.deviceInfos.map(
                              (
                                {
                                  _id,
                                  deviceId,
                                  language,
                                  appVersion,
                                  osVersion,
                                  platform,
                                },
                                index
                              ) => (
                                <tr key={_id}>
                                  <td>{index + 1}</td>
                                  <td>{deviceId || "-"}</td>
                                  <td>{formatDeviceLanguageLabel(language)}</td>
                                  <td>
                                    {appVersion
                                      ? formatAppVersionLabel(appVersion)
                                      : "-"}
                                  </td>
                                  <td>{osVersion || "-"}</td>
                                  <td>
                                    {platform
                                      ? String(platform).toLowerCase() ===
                                        "android"
                                        ? t("Android")
                                        : String(platform).toLowerCase() ===
                                          "ios"
                                        ? t("iOS")
                                        : t(platform)
                                      : "-"}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="my-3 text-center">
                          {t("No device info found")}
                        </p>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}

              {(isTenantOwnerAdmin() ||
                (customersPermissions.list &&
                  customerSubPermissions?.view_orders?.isEnabled)) && (
                <Tab eventKey="ORDERS" title={t("Orders")}>
                  <Card>
                    <CardBody className="overflow-auto">
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 600 }}
                      >
                        {orderLoading ? (
                          <TableShimmer row={5} col={5} />
                        ) : orders.length === 0 ? (
                          <p className="text-center my-2">
                            {t("No orders found")}
                          </p>
                        ) : (
                          <Table className="table mb-0">
                            <thead className="fixed_thead full_th">
                              <tr>
                                <th>{t("S. No.")}</th>
                                <th>{t("Name")}</th>
                                <th>{t("Order No")}</th>
                                {hasPos && <th>{t("Service Provider")}</th>}
                                <th>{t("Branch Name")}</th>
                                <th>{t("Amount Paid")}</th>
                                <th>{t("Payment Method")}</th>
                                <th>{t("Order Status")}</th>
                                {/* <th>{t("Payment Status")}</th> */}
                                <th>{t("Order Date")}</th>
                                <th>{t("Recipient Address")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orders?.map((item, index) => {
                                const createdOrderDate = new Date(
                                  item?.createdAt
                                )
                                const formattedcreatedOrderDate =
                                  createdOrderDate?.toLocaleString(
                                    language === "sa" ? "ar-SA" : "en-US",
                                    {
                                      weekday: "short", // Sun
                                      year: "numeric", // 2025
                                      month: "short", // Aug
                                      day: "numeric", // 31
                                      hour: "2-digit", // 03
                                      minute: "2-digit", // 46
                                      second: "2-digit", // 55
                                      hour12: true, // AM/PM
                                    }
                                  )
                                const serviceProviderLabel =
                                  resolveOrderServiceProvider(item)
                                const orderStatusDisplay =
                                  getOrderStatusDisplay(item)
                                return (
                                  <tr
                                    key={item._id}
                                    onClick={() => navigateToOrderDetails(item)}
                                    className="cursor-pointer"
                                  >
                                    <td>
                                      {(pageNumber - 1) * itemsPerPage +
                                        index +
                                        1}
                                    </td>
                                    <td>{item?.name || "-"}</td>
                                    <td>{item?.invoiceNo || "-"}</td>
                                    {hasPos && (
                                      <td className="text-capitalize">
                                        {serviceProviderLabel
                                          ? t(serviceProviderLabel)
                                          : "-"}
                                      </td>
                                    )}

                                    <td>
                                      {language === "sa"
                                        ? item?.branchNameAr
                                        : item?.branchName || "-"}
                                    </td>

                                    <td>
                                      {item.paidAmount} (
                                      {t(item?.splitPayments?.currency)})
                                    </td>
                                    <td>
                                      <i
                                        className={`${
                                          item.paymentMethod === "CARD"
                                            ? "fab fa-cc-mastercard me-1"
                                            : "" ||
                                              item.paymentMethod === "APPLE"
                                            ? "fab bx bxl-apple me-1"
                                            : ""
                                        }`}
                                      ></i>
                                      {t(item.paymentMethod)}
                                    </td>
                                    <td>
                                      <span
                                        style={{
                                          color: orderStatusDisplay.color,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {orderStatusDisplay.text || "-"}
                                      </span>
                                    </td>
                                    {/* <td>
                                      {" "}
                                      {item.paymentStatus === 1 && (
                                        <Badge className="bg-success">
                                          {t("Paid")}
                                        </Badge>
                                      )}
                                      {item.paymentStatus === 2 && (
                                        <Badge className="bg-success">
                                          {"Refunded"}
                                        </Badge>
                                      )}
                                    </td> */}

                                    <td>
                                      {/* {formatPromiseDateTime(item?.createdAt)} */}
                                      {formattedcreatedOrderDate}
                                    </td>
                                    <td>
                                      {[
                                        item?.recipientInformation?.address
                                          ?.address1 ?? "",
                                        item?.recipientInformation?.address
                                          ?.address2 ?? "",
                                        item?.recipientInformation?.address
                                          ?.city ?? "",
                                        item?.recipientInformation?.address
                                          ?.district ?? "",
                                        item?.recipientInformation?.address
                                          ?.country ?? "",
                                      ]
                                        .filter(Boolean) // removes empty/null/undefined values
                                        .join(" , ")}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      {!orderLoading && orders.length > 0 && (
                        <div className="mt-3">
                          <Paginations
                            data={orders}
                            itemsPerPage={itemsPerPage}
                            onPageChange={onPageChange}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}
              {(isTenantOwnerAdmin() ||
                (customersPermissions.list &&
                  customerSubPermissions?.view_loyalty_points?.isEnabled)) && (
                <Tab eventKey="LOYALTY" title={t("Loyalty Points")}>
                  <Card>
                    <CardBody className="overflow-auto">
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 600 }}
                      >
                        {loyaltyLoading ? (
                          <TableShimmer row={5} col={5} />
                        ) : customerLoyaltyPoints.length === 0 ? (
                          <p className="text-center my-2">
                            {t("No loyalty points found")}
                          </p>
                        ) : (
                          <Table className="table mb-0 capitalize_td">
                            <thead className="fixed_thead">
                              <tr>
                                <th>{t("S. No.")}</th>
                                <th>{t("Name")}</th>
                                <th>{t("Phone Number")}</th>
                                <th>{t("Order No")}</th>
                                <th>{t("Points")}</th>
                                <th>{t("Type")}</th>
                                <th>{t("Source")}</th>
                                <th>{t("Expires At")}</th>
                                <th>{t("Created At")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customerLoyaltyPoints?.map((item, index) => (
                                <tr key={item._id}>
                                  <td>
                                    {(pageNumber - 1) * itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td>{item?.userData?.name}</td>
                                  <td>
                                    {item?.userData?.phoneNumber ? (
                                      <LtrText>
                                        {item.userData.phoneNumber}
                                      </LtrText>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>{item?.orderNo || "-"}</td>
                                  <td>{item?.points || "-"}</td>
                                  <td
                                    className={`
                                ${
                                  item?.type === "collect" ? "text-success" : ""
                                }
                                ${item?.type === "redeem" ? "text-danger" : ""}
                                ${item?.type === "cancel" ? "text-warning" : ""}
                                `}
                                  >
                                    {t(item?.type)}
                                  </td>
                                  <td>
                                    {item?.source ? t(item.source) : "-"}
                                  </td>
                                  <td>{formatDateTime(item?.expiresAt)}</td>
                                  <td>{formatDateTime(item?.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      {!loading && customerLoyaltyPoints.length > 0 && (
                        <div className="mt-3">
                          <Paginations
                            data={customerLoyaltyPoints}
                            itemsPerPage={itemsPerPage}
                            onPageChange={onLoyaltyPageChange}
                            currentPage={loyaltyCurrentPage}
                            totalPages={loyaltyTotalPages}
                            totalCount={loyaltyTotalCount}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}
              {(isTenantOwnerAdmin() ||
                (customersPermissions.list &&
                  customerSubPermissions?.view_payment_history?.isEnabled)) && (
                <Tab eventKey="PAYMENT_HISTORY" title={t("Payment History")}>
                  <Card>
                    <CardBody className="overflow-auto">
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 600 }}
                      >
                        {paymentHistoryLoading ? (
                          <TableShimmer row={5} col={5} />
                        ) : paymentHistory.length === 0 ? (
                          <p className="text-center my-2">
                            {t("No payment history found")}
                          </p>
                        ) : (
                          <Table className="table mb-0 capitalize_td">
                            <thead className="fixed_thead">
                              <tr>
                                <th>{t("S. No.")}</th>
                                <th>{t("Invoice No")}</th>
                                <th>{t("Branch Name")}</th>
                                {/* <th>{t("Order No")}</th> */}
                                <th>{t("Amount")}</th>
                                <th>{t("Type")}</th>
                                <th>{t("Payment Method")}</th>
                                <th>{t("Gateway")}</th>
                                <th>{t("Gateway Status")}</th>
                                <th>{t("Created")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentHistory?.map((item, index) => (
                                <tr key={item._id}>
                                  <td>
                                    {(pageNumber - 1) * itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td>{item?.invoiceNo}</td>
                                  <td>
                                    {language === "sa"
                                      ? item?.branchNameAr ||
                                        item?.branchName ||
                                        "-"
                                      : item?.branchName || "-"}
                                  </td>
                                  {/* <td>{item?.orderId || "-"}</td> */}
                                  <td>
                                    {item?.amount != null
                                      ? `${item.amount} ${t("SAR")}`
                                      : "-"}
                                  </td>
                                  <td>
                                    {item?.type
                                      ? t(formatType(item.type))
                                      : "-"}
                                  </td>
                                  <td>
                                    {item?.paymentMethod
                                      ? t(item.paymentMethod)
                                      : "-"}
                                  </td>
                                  <td>
                                    {item?.gateway
                                      ? translatePaymentGateway(t, item.gateway)
                                      : "-"}
                                  </td>
                                  <td>
                                    {item?.gatewayStatus
                                      ? translateGatewayStatus(
                                          t,
                                          item.gatewayStatus
                                        )
                                      : "-"}
                                  </td>
                                  <td>{formatDateTime(item?.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      {!loading && paymentHistory.length > 0 && (
                        <div className="mt-3">
                          <Paginations
                            data={paymentHistory}
                            itemsPerPage={itemsPerPage}
                            onPageChange={onPaymentHistoryPageChange}
                            currentPage={paymentCurrentPage}
                            totalPages={paymentTotalPages}
                            totalCount={paymentTotalCount}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}
              {/*  */}
              {canViewWalletHistory && (
                <Tab eventKey="WALLET_HISTORY" title={t("Wallet History")}>
                  <Card>
                    <CardBody className="overflow-auto">
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 600 }}
                      >
                        {walletHistoryLoading ? (
                          <TableShimmer row={5} col={5} />
                        ) : walletHistory?.length === 0 ? (
                          <p className="text-center my-2">
                            {t("No wallet history found")}
                          </p>
                        ) : (
                          <Table className="table mb-0 capitalize_td">
                            <thead className="fixed_thead">
                              <tr>
                                <th>{t("Transaction No")}</th>
                                <th>{t("Type")}</th>
                                <th>{t("Amount")}</th>
                                <th>{t("From Name")}</th>
                                <th>{t("Points")}</th>
                                <th>{t("Source")}</th>
                                <th>{t("Created At")}</th>
                                <th>{t("Transaction Id")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {walletHistory?.map((item, index) => (
                                <tr key={item._id}>
                                  <td>{item?.txnNo}</td>
                                  <td>{item?.type || "-"}</td>
                                  <td>{item?.amount ?? "-"}</td>
                                  <td>{item?.fromName || "-"}</td>
                                  <td>{item?.points ?? "-"}</td>
                                  <td>{item?.source || "-"}</td>
                                  <td>{formatDateTime(item?.createdAt)}</td>
                                  <td>
                                    {item?.transactionDetails?.transactionId ??
                                      "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      {!loading && walletHistory?.length > 0 && (
                        <div className="mt-3">
                          <Paginations
                            data={walletHistory}
                            itemsPerPage={itemsPerPage}
                            onPageChange={onWalletHistoryPageChange}
                            currentPage={walletCurrentPage}
                            totalPages={walletTotalPages}
                            totalCount={walletTotalCount}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}
              {canViewGiftCardUsage && (
                <Tab eventKey="GIFT_CARD_USAGE" title={t("Gift Card Usage")}>
                  <Card>
                    <CardBody className="overflow-auto">
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 600 }}
                      >
                        {giftCardLoading ? (
                          <TableShimmer row={5} col={5} />
                        ) : giftCardsUsage?.length === 0 ? (
                          <p className="text-center my-2">
                            {t("No gift card usage found")}
                          </p>
                        ) : (
                          <Table className="table mb-0 capitalize_td">
                            <thead className="fixed_thead">
                              <tr>
                                <th>{t("Gift Card No")}</th>
                                <th>{t("Gift Card Name")}</th>
                                <th>{t("Redeemed")}</th>
                                <th>{t("Sender Name")}</th>
                                <th>{t("Receiver Name")}</th>
                                <th>{t("Amount")}</th>
                                <th>{t("Expires At")}</th>
                                <th>{t("Message")}</th>
                                <th>{t("Transaction Id")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {giftCardsUsage?.map((item, index) => (
                                <tr
                                  key={item._id}
                                  onClick={() => {
                                    navigate(
                                      `/giftcard-reports/details?id=${item._id}`
                                    )
                                  }}
                                  className="cursor-pointer"
                                >
                                  <td>{item?.giftCardNo ?? "-"}</td>
                                  <td>
                                    {language === "sa"
                                      ? item.giftCardId?.nameAr
                                      : item.giftCardId?.name || "-"}
                                  </td>
                                  <td>
                                    {item.redeemed ? (
                                      <Badge className="bg-success">
                                        {t("Redeemed")}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-danger">
                                        {t("Not Redeem")}
                                      </Badge>
                                    )}
                                  </td>
                                  <td>{item?.senderId?.name || "-"}</td>
                                  <td>{item?.receiverName || "-"}</td>
                                  <td>
                                    {item?.amount ?? "-"} {item?.currency}
                                  </td>
                                  <td>{formatDateTime(item?.expiresAt)}</td>
                                  <td style={{ maxWidth: 230 }}>
                                    <p className="in_2_line">{item?.message}</p>
                                  </td>
                                  <td>
                                    {item?.transactionDetails?.transactionId ??
                                      "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      {!giftCardLoading && giftCardsUsage?.length > 0 && (
                        <div className="mt-3">
                          <Paginations
                            data={giftCardsUsage}
                            itemsPerPage={giftCardItemsPerPage}
                            onPageChange={onGiftCardUsagePageChange}
                            currentPage={giftCardCurrentPage}
                            totalPages={giftCardTotalPages}
                            totalCount={giftCardTotalCount}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Tab>
              )}
            </Tabs>
          </Col>
        </Row>
      </div>

      <Modal
        isOpen={showModal}
        toggle={() => {
          setShowModal(false)
          setModalType(null)
        }}
      >
        <ModalHeader
          toggle={() => {
            setShowModal(false)
            setModalType(null)
          }}
        >
          {modalType === "ADD_LOYALTY_POINTS" && t("Add Loyalty Points")}
          {modalType === "REVOKE_LOYALTY_POINTS" && t("Revoke Loyalty Points")}
        </ModalHeader>
        <ModalBody>
          {modalType === "REVOKE_LOYALTY_POINTS" && (
            <Formik
              initialValues={initialValues}
              validationSchema={revokeLoyaltyPointsSchema}
              onSubmit={handleRevokeLoyaltyPoints}
            >
              {({ values, errors, touched, setFieldValue }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-12 ">
                      <div className="form_field">
                        <label htmlFor="points">
                          <span className="text-danger">*</span> {t("Points")}
                        </label>
                        <Field
                          type="number"
                          id="points"
                          name="points"
                          placeholder={t("Points")}
                          className={`form-control ${
                            touched.points && errors.points ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="points" />
                      </div>
                    </div>

                    <div className="col-lg-12 mt-2">
                      <div className="form_field">
                        <label htmlFor="remarks">
                          <span className="text-danger">*</span> {t("Remark")}
                        </label>
                        <Field
                          as="textarea"
                          id="remarks"
                          name="remarks"
                          maxLength={LOYALTY_REMARK_MAX_LENGTH}
                          placeholder={t("Remark")}
                          className={`form-control ${
                            touched.remarks && errors.remarks
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="remarks" />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        color="secondary"
                        type="button"
                        onClick={() => {
                          setShowModal(false)
                          setModalType(null)
                        }}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        color="success"
                        className="ms-2"
                        disabled={isLoading}
                      >
                        {t("Revoke")}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {modalType === "ADD_LOYALTY_POINTS" && (
            <Formik
              initialValues={addLoyaltyPointsValues}
              validationSchema={addLoyaltyPointSchema}
              onSubmit={handleAddLoyaltyPoints}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="form_field">
                        <label htmlFor="points">
                          <span className="text-danger">*</span> {t("Points")}
                        </label>
                        <Field
                          type="number"
                          id="points"
                          name="points"
                          placeholder={t("Points")}
                          className={`form-control ${
                            touched.points && errors.points ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="points" />
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="form_field full_rs_picker">
                        <label htmlFor="expiresAt">{t("Expires At")}</label>
                        <DatePicker
                          editable={false}
                          showTime={{ format: "HH:mm" }}
                          format="yyyy-MM-dd HH:mm"
                          onChange={value => {
                            setFieldValue("expiresAt", value || null)
                          }}
                          value={values.expiresAt || null}
                          placeholder={t("Select date and time")}
                          shouldDisableDate={disablePastDateTime.shouldDisableDate}
                          shouldDisableHour={disablePastDateTime.shouldDisableHour}
                          shouldDisableMinute={
                            disablePastDateTime.shouldDisableMinute
                          }
                        />
                        <TextError name="expiresAt" />
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="form_field">
                        <label htmlFor="name">
                          <span className="text-danger">*</span> {t("Remark")}
                        </label>
                        <Field
                          as="textarea"
                          id="remarks"
                          name="remarks"
                          maxLength={LOYALTY_REMARK_MAX_LENGTH}
                          placeholder={t("Remark")}
                          className={`form-control ${
                            touched.remarks && errors.remarks
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="remarks" />
                      </div>
                    </div>

                    <div className="mt-2"></div>
                    <div className="col-12 d-flex justify-content-end ">
                      <Button
                        type="button"
                        className="mx-2"
                        color="secondary"
                        onClick={() => {
                          setShowModal(false)
                          setModalType(null)
                        }}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="success"
                        disabled={isLoading}
                        className="w_fit_content"
                      >
                        {t("Add")}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </ModalBody>
      </Modal>
      <DeleteModal
        show={showBlockModal}
        isLoading={isUpdating}
        onDeleteClick={handleConfirmBlockAction}
        onCloseClick={() => {
          setShowBlockModal(false)
          setBlockTarget(null)
        }}
        buttonText={blockModalButton}
        bodyTitle={blockModalTitle}
        icon={blockModalIcon}
        btnClass={blockModalBtnClass}
      />
    </div>
  )
}

export default CustomersView
